/**
 * pw-analytics-gate.js -- tasks.db #1722 (2026-09-29): the ONE place this theme calls gtag.
 *
 * THE BUG: the Google & YouTube app injects the LIVE Google tag (GA4 G-BYXB6BCMDW + Google Ads
 * AW-11304447747) into every theme that renders, published or not. So a signup on the STAGING
 * preview fired the live `generate_lead` conversion -- Cowork's 2026-09-29 staging delivery test
 * logged 3 fake conversions in one afternoon, feeding Ads bidding fabricated leads.
 *
 * THE FIX: every theme-originated gtag event goes through `PWAnalyticsGate.gtagEvent(name,
 * params)`, which drops it unless the page is rendered by the LIVE theme. Suppressed events are
 * pushed onto `PWAnalyticsGate.suppressed` and logged once to the console, so a staging test can
 * SEE that the conversion was intentionally withheld rather than wondering whether it broke.
 *
 * How "live" is decided -- by ROLE, never by theme NAME (tasks.db #1659: names churn and carry
 * timestamps; the live theme was once literally named "STAGING"):
 *   - Theme Editor (Liquid `request.design_mode`, stamped as window.PW_DESIGN_MODE by
 *     snippets/pw-analytics-gate.liquid, or Shopify.designMode)       -> suppress
 *   - Shopify.theme.role present and not 'main' (unpublished = a preview_theme_id preview,
 *     development = `shopify theme dev`, demo)                         -> suppress
 *   - Shopify.theme.role === 'main'                                    -> fire
 *   - no role at all                                                   -> fire (fail OPEN)
 * Shopify stamps `Shopify.theme = {..., "role": "..."}` into content_for_header on every render
 * (verified on both the live storefront and the staging preview, 2026-09-29). Fail-open on a
 * missing role is deliberate: suppression needs POSITIVE evidence of a non-live render, so a
 * Shopify-side change to that object can never silently zero the live Ads conversion stream.
 *
 * The role is read LAZILY on every call: this script is deferred from <head> and may run before
 * or after content_for_header's inline script, but every event it gates fires later (on submit,
 * or at DOMContentLoaded).
 *
 * Scope: this governs only gtag calls made by THIS theme's own assets. It does NOT govern the
 * theme's `Shopify.analytics.publish` custom events (pw-site-header, pw-saturday-spark,
 * pw-scope-banner): those go to Shopify web pixels, not GA4, today -- if a custom pixel ever
 * forwards them to GA4/Ads, route them through this gate too. The Google & YouTube,
 * TikTok and Pinterest app pixels (Shopify web pixels, sandboxed) also load on unpublished themes
 * and send their own page_view etc.; a theme cannot reach inside them.
 *
 * Exported via a `typeof module` guard so tests/mobile/lib/analytics-gate.test.mjs can `require`
 * the real decide() -- inert in the browser, where `module` is undefined.
 */
(function (factory) {
  var api = factory();
  // Attach to window UNCONDITIONALLY (3-model review fix): an either/or UMD guard would skip this
  // whenever some app script leaks a global `module` (legacy CommonJS shims do), and every call
  // site fails closed without the gate -- all live conversions would vanish, silently.
  // In a browser it never writes to a leaked `module.exports` either (that would clobber some other
  // script's object); the module export is for node tests only, where there is no `window`.
  if (typeof window !== 'undefined') window.PWAnalyticsGate = api.browser(window);
  else if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(function () {
  'use strict';

  var LIVE_ROLE = 'main';

  /** env: { shopify: window.Shopify, designMode: window.PW_DESIGN_MODE } -> { allowed, reason } */
  function decide(env) {
    var shopify = (env && env.shopify) || null;
    if ((env && env.designMode === true) || (shopify && shopify.designMode === true)) {
      return { allowed: false, reason: 'design_mode' };
    }
    var role = shopify && shopify.theme ? shopify.theme.role : undefined;
    if (typeof role === 'string' && role !== '') {
      return role === LIVE_ROLE ? { allowed: true, reason: 'live' } : { allowed: false, reason: 'non_live_role:' + role };
    }
    return { allowed: true, reason: 'role_unknown' };
  }

  function browser(win) {
    var logged = false;
    var warned = false;
    var gate = {
      suppressed: [],
      decide: function () {
        return decide({ shopify: win.Shopify, designMode: win.PW_DESIGN_MODE });
      },
      /** Sends gtag('event', name, params) only on the live theme. Returns true if sent. */
      gtagEvent: function (name, params) {
        var d = gate.decide();
        if (!d.allowed) {
          gate.suppressed.push({ event: name, reason: d.reason });
          if (!logged && typeof console !== 'undefined' && console.info) {
            logged = true;
            console.info('[pw-analytics-gate] not the live theme (' + d.reason + '): analytics/conversion events are withheld on this page. See PWAnalyticsGate.suppressed.');
          }
          return false;
        }
        if (typeof win.gtag !== 'function') return false; // ad-blocked or the Google app absent
        try {
          if (params === undefined) win.gtag('event', name);
          else win.gtag('event', name, params);
          return true;
        } catch (e) {
          // Analytics must never break the page -- but never fail silently either (reported once).
          if (!warned && typeof console !== 'undefined' && console.warn) {
            warned = true;
            console.warn('[pw-analytics-gate] gtag threw; event not sent', e);
          }
          return false;
        }
      },
    };
    return gate;
  }

  return { decide: decide, browser: browser, LIVE_ROLE: LIVE_ROLE };
});
